import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect from "use-deep-compare-effect";
import { z } from "zod";

import type { DraftPayment, Payment } from "@/apis/gql/types";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { TextInput } from "@/components/shared/form/text-input";
import { Form as FormProvider } from "@/components/ui/form";
import {
  INVOICE_QUERY,
  type InvoiceDetailsDraftPayment,
} from "@/queries/invoices/get";
import { useCreatePayment } from "@/queries/payments/create-payment";
import { useCreateDraftPayment } from "@/queries/payments/draft-payments/create-draft-payment";
import { useUpdateDraftPayment } from "@/queries/payments/draft-payments/update-draft-payment";
import { useUpdatePayment } from "@/queries/payments/update-payment";

type Props = {
  draftPayments: InvoiceDetailsDraftPayment[];
  invoiceId: string;
  currency: string;
  amount: number;
  onValuesChange?: (values: Partial<PaymentFormValues>) => void;
  onFieldFocus: (field: string) => void;
};

export const EditPaymentReferenceForm = ({
  draftPayments,
  invoiceId,
  currency,
  amount,
  onValuesChange,
  onFieldFocus,
}: Props) => {
  const [createDraftPayment] = useCreateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });
  const { updateDraftPayment } = useUpdateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });

  const handleCreatePayment = (values: PaymentFormValues) => {
    createDraftPayment(invoiceId, {
      purpose: values.purpose,
      rawAmount: amount,
      currency,
    });
  };

  const handleUpdatePayment = (values: PaymentFormValues) => {
    updateDraftPayment({
      draftPaymentId: values.draftPaymentId,
      purpose: values.purpose,
    });
  };

  if (draftPayments.length === 0) {
    return (
      <Form
        values={{}}
        onSubmit={handleCreatePayment}
        onValuesChange={onValuesChange}
        onFieldFocus={onFieldFocus}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {draftPayments.map((payment, index) => (
        <Form
          key={payment.draftPaymentId}
          index={index + 1}
          values={{
            ...payment,
          }}
          onSubmit={handleUpdatePayment}
          onValuesChange={onValuesChange}
          onFieldFocus={onFieldFocus}
        />
      ))}
    </div>
  );
};

const paymentFormSchema = z.object({
  draftPaymentId: z.string().optional(),
  purpose: z
    .string()
    .trim()
    .min(1, {
      message: i18next.t("payment_validation_error.purpose.required"),
    })
    .max(140, {
      message: i18next.t("payment_validation_error.purpose.max_length"),
    })
    .optional()
    .nullable(),
});

type PaymentFormValues = z.infer<typeof paymentFormSchema>;

type FormProps = {
  values: PaymentFormValues;
  index?: number;
  disabled?: boolean;
  onSubmit: (values: PaymentFormValues) => void;
  onValuesChange?: (values: PaymentFormValues) => void;
  onFieldFocus: (field: string) => void;
};

const Form = ({
  values,
  disabled = false,
  onSubmit,
  onValuesChange,
  index,
  onFieldFocus,
}: FormProps) => {
  const { t } = useTranslation();

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    values,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const formValues = form.watch();

  const isDirty = form.formState.isDirty;

  const handleSubmit = (values: PaymentFormValues) => {
    onSubmit(values);
  };

  useEffect(() => {
    form.trigger();
  }, []);

  useDeepCompareEffect(() => {
    if (isDirty) {
      onValuesChange?.(formValues);
    }
  }, [formValues, onValuesChange, isDirty]);

  return (
    <FormProvider {...form}>
      <form className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <TextInput
            optional
            label={t("payment_details.fields.purpose")}
            name="purpose"
            disabled={disabled}
            onFocus={() => onFieldFocus("paymentPurpose")}
          />
        </div>
        <AutoSaveForm onSubmit={handleSubmit} immediate />
      </form>
    </FormProvider>
  );
};

export type EditPaymentReferenceFormValues = PaymentFormValues;
